import React, { useState, useContext, useEffect, useCallback } from 'react';
import Dialog from '../../../../../../../../../../BaseComponents/Dialog/EDDialog';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EkonomiskAnalysColumnSelect from './Components/EkonomiskAnalysColumnSelect/EkonomiskAnalysColumnSelect';
import { IColumn } from '../../../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { IColumnTemplate } from '../Queries/AvailableColumnTemplatesHandler';
import { IEkonomiskAnalysDates } from '../../../../AnalysSettingsHandler/AnalysSettingsHandler';
import EkonomiskAnalysEditColumnReferencePeriod from '../EditColumn/Components/EkonomiskAnalysEditColumnReferencePeriod/EkonomiskAnalysEditColumnReferencePeriod';
import {
  getDateInputType,
  hasAnyInterval,
} from '../../../../../Utils/analysUtils';
import { EkonomiskAnalysContext } from '../../../../Context/EkonomiskAnalysContext';
import { getDefaultDate } from '../../../../../../../../../../Utils/dateUtils';
import { ISelectedAnalys } from '../../../../../EkonomiskAnalysPage';

interface IProps {
  selectedColumns: IColumn[];
  dates: IEkonomiskAnalysDates;
  dialogOpen: boolean;
  dialogOnClose: () => void;
  onUpdate: (columns: IColumn[]) => void;
  isInterval: boolean;
  availableColumnTemplates: IColumnTemplate[];
  selectedAnalys: ISelectedAnalys | null;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      dialogPaper: {
        height: '80%',
        maxHeight: '800px',
        maxWidth: '700px',
        [theme.breakpoints.down('md')]: {
          maxHeight: 'none',
          maxWidth: 'none',
          height: '100%',
        },
      },
      content: {
        display: 'flex',
      },
      contentWrapper: {
        margin: 0,
        flex: '0 1 100%',
        overflowX: 'hidden',
      },
    } as const)
);

/**
 * En dialog där kolumnerna för en analys kan ändras.
 */
const EkonomiskAnalysColumnSelectDialog: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes } = useStyles();
  const { standardColumns } = useContext(EkonomiskAnalysContext);
  const [selectColumns, setSelectColumns] = useState(props.selectedColumns);
  const [referenceDates, setReferenceDates] = useState<
    IEkonomiskAnalysDates | undefined
  >(undefined);

  useEffect(() => {
    setReferenceDates(props.dates);
  }, [props.dates]);

  const [selectedTemplateColumns, setSelectedTemplateColumns] = useState<
    IColumn[]
  >([]);
  const setInitialState = () => {
    setSelectColumns(props.selectedColumns);
    setSelectedTemplateColumns([]);
    correctDateFormatForIntervalType();
  };

  const getUsedColumns = useCallback(() => {
    return selectColumns.length > 0 ? selectColumns : selectedTemplateColumns;
  }, [selectColumns, selectedTemplateColumns]);

  const correctDateFormatForIntervalType = useCallback(() => {
    const dateInputType = getDateInputType(getUsedColumns(), standardColumns);

    const newReferenceDate: IEkonomiskAnalysDates = getDefaultDate(
      props.dates,
      dateInputType
    );
    setReferenceDates(newReferenceDate);
  }, [props.dates, standardColumns, getUsedColumns]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(setInitialState, [props.selectedColumns]);
  useEffect(() => {
    correctDateFormatForIntervalType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectColumns, selectedTemplateColumns]);

  const selectedColumnsUpdate = (columns: IColumn[]) => {
    setSelectColumns(columns);
  };

  const selectedTemplateColumnsUpdate = (columns: IColumn[]) => {
    setSelectedTemplateColumns(columns);
  };

  const onExited = () => {
    setInitialState();
  };

  const onComplete = () => {
    const updateColumns =
      selectColumns.length > 0 ? selectColumns : selectedTemplateColumns;
    props.onUpdate(updateColumns);
    props.dialogOnClose();
  };

  const { dialogOpen, dialogOnClose } = props;
  const { dialogPaper, ...muiClasses } = classes;
  return (
    <Dialog
      title="Välj kolumner"
      open={dialogOpen}
      onClose={dialogOnClose}
      fullWidth
      footerActions={[
        {
          label: 'Avbryt',
          onClick: dialogOnClose,
        },
        {
          label: 'Använd',
          onClick: onComplete,
          buttonProps: {
            disabled:
              selectColumns.length === 0 &&
              selectedTemplateColumns.length === 0,
          },
        },
      ]}
      subtitle={
        <EkonomiskAnalysEditColumnReferencePeriod
          dates={referenceDates}
          isInterval={hasAnyInterval(getUsedColumns(), standardColumns)}
        />
      }
      classes={{ paper: dialogPaper, ...muiClasses }}
      onExited={onExited}
    >
      <EkonomiskAnalysColumnSelect
        selectedColumns={selectColumns}
        selectedTemplateColumns={selectedTemplateColumns}
        availableColumnTemplates={props.availableColumnTemplates}
        codePrivilege={
          props.selectedAnalys ? props.selectedAnalys.codePrivilege.id : ''
        }
        analysName={props.selectedAnalys ? props.selectedAnalys.analys.id : ''}
        dates={props.dates}
        selectedColumnsUpdate={selectedColumnsUpdate}
        selectedTemplateColumnsUpdate={selectedTemplateColumnsUpdate}
        isInterval={props.isInterval}
      />
    </Dialog>
  );
};

export default EkonomiskAnalysColumnSelectDialog;
